import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalController, ToastController } from '@ionic/angular';
import { AuthService } from 'src/app/core/authentication/services/auth.service';
import { DateHelperService } from 'src/app/core/helpers/date-helper/date-helper.service';
import { FormHelperService } from 'src/app/core/helpers/form-helper/form-helper.service';
import { AppointmentTimeEntry } from 'src/app/core/models/appointment-time-entry.model';
import { Appointment } from 'src/app/core/models/appointment.model';
import { ConfigSetting } from 'src/app/core/models/config-settings.model';
import { CustomerService } from 'src/app/core/models/customer-service.model';
import { PayCode } from 'src/app/core/models/pay-codes.model';
import { TimeCode } from 'src/app/core/models/time-codes.model';
import { TimeEntry } from 'src/app/core/models/time-entry.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { UserService } from 'src/app/core/services/user/user-service';
import { TimeEntryService } from 'src/app/core/time-entry/time-entry.service';

@Component({
	selector: 'app-time-tracking-form',
	templateUrl: './time-tracking-form.component.html',
	styleUrls: ['./time-tracking-form.component.scss']
})
export class TimeTrackingFormComponent implements OnInit {

	@Input()
	mode = 'Add'; // Add || Edit

	@Input()
	timeEntry: TimeEntry;

	@Input()
	apptTimeEntry: AppointmentTimeEntry;

	@Input()
	hidden = [] as string[];

	@Input()
	DisableEditOnCleared: ConfigSetting;

	pageTitle = 'Time Entry';

	form: FormGroup;

	// Dropdown options
	appointmentOptions: Appointment[];
	timeCodeOptions: TimeCode[];
	payCodeOptions: PayCode[];
	serviceOptions: CustomerService[];

	// Defaults
	appointmentOptionDefault = new Appointment({ id: -2000, displayValue: 'Other' });
	serviceOptionDefault = new CustomerService();
	timeOptionDefault = '00:00';
	capitalOptionDefault = false;

	constructor(
		private modalController: ModalController,
		private formBuilder: FormBuilder,
		public formHelper: FormHelperService,
		private appointmentService: AppointmentService,
		private userService: UserService,
		private dateHelper: DateHelperService,
		private timeEntryService: TimeEntryService,
		private authService: AuthService,
		private toastController: ToastController,
	) { }

	isHidden(inputName: string): boolean {
		return this.hidden.includes(inputName);
	}

	async ngOnInit(): Promise<void> {
		await this.setOptions();
		this.setForm();
	}

	async setForm(): Promise<void> {
		this.form = this.formBuilder.group({
			appointment: [await this.initAppointmentField(), Validators.required],
			timeCode: [await this.initTimeCodeField(), Validators.required],
			payCode: [await this.initPayCodeField(), Validators.required],
			service: [await this.initServiceField()],
			time: [this.initTimeField(), Validators.required],
			capital: [this.initCapitalField(), Validators.required],
			note: [this.initNoteField()],
		});

		if (this.mode === 'Edit') {
			this.form.controls.appointment.disable();
		}
	}

	async initAppointmentField(): Promise<Appointment> {
		if (this.apptTimeEntry == null || this.apptTimeEntry.apptId === -2000) {
			return this.appointmentOptionDefault;
		}

		return await this.appointmentService.getLocalAppointment(this.apptTimeEntry.apptId);
	}

	async initTimeCodeField(): Promise<TimeCode> {
		if (this.mode === 'Add') {
			return (null as TimeCode);
		}

		return (await this.userService.getCurrentUserTimeCodes()).find(tc => tc.id === this.timeEntry.timeCodeId);
	}

	async initPayCodeField(): Promise<PayCode> {
		if (this.mode === 'Add') {
			return this.payCodeOptions[0];
		}

		return (await this.userService.getCurrentUserPayCodes()).find(pc => pc.id === this.timeEntry.payCodeId);
	}

	async initServiceField(): Promise<CustomerService> {
		if (this.mode === 'Add' || this?.timeEntry?.customerServiceId === -100) {
			return this.serviceOptionDefault;
		}

		const services = this.getServiceOptions(await this.initAppointmentField());
		return services.find(s => s.id === this.timeEntry.customerServiceId);
	}

	initTimeField() {
		if (this.mode === 'Add') {
			return this.timeOptionDefault;
		}

		const time = this.dateHelper.getHoursMinutesFromMinutes(this.timeEntry.minutes);
		const hours = (time.hours < 10) ? `0${time.hours}` : `${time.hours}`;
		const minutes = (time.minutes < 10) ? `0${time.minutes}` : `${time.minutes}`;

		return `${hours}:${minutes}`;
	}

	initCapitalField() {
		if (this.mode === 'Add') {
			return this.capitalOptionDefault;
		}

		return this.timeEntry.capital;
	}

	initNoteField() {
		if (this.mode === 'Add') {
			return '';
		}

		return this.timeEntry.note;
	}

	async setOptions(): Promise<void> {
		await Promise.all([
			this.setAppointmentOptions(),
			this.setPayCodeOptions(),
			this.setTimeCodeOptions(!!this.apptTimeEntry?.apptId),
		]);

		this.serviceOptions = this.getServiceOptions(await this.initAppointmentField());
	}

	async setAppointmentOptions(): Promise<void> {
		let appts = await this.appointmentService.getAllAppointments();
		appts.sort((b, a) => new Date(b.apptStart).getTime() - new Date(a.apptStart).getTime());

		if (this.mode === 'Add') {
			if (this.DisableEditOnCleared?.value === 'True') {
				appts = appts.filter(appt => !appt.ticketStatusIsEndStatus);
			}
		}

		this.appointmentOptions = appts;
	}

	async setPayCodeOptions(): Promise<void> {
		this.payCodeOptions = await this.userService.getCurrentUserPayCodes();
	}

	async setTimeCodeOptions(hasAppointment = false): Promise<void> {
		const timeCodes = await this.userService.getCurrentUserTimeCodes();

		if (!hasAppointment) {
			this.timeCodeOptions = timeCodes.filter(tc => tc.noAppt);
			return;
		}

		this.timeCodeOptions = timeCodes.filter(tc => tc.appt);
	}

	async onChangeAppointment(value: Appointment): Promise<void> {
		if (!value) {
			return;
		}
		this.form.controls.timeCode.reset();

		if (value === this.appointmentOptionDefault) {
			return await this.handleRemovedAppointment();
		}

		return this.handleAddedAppointment();
	}

	async handleRemovedAppointment(): Promise<void> {
		await this.setTimeCodeOptions(false);

		this.serviceOptions = [];
		this.form.controls.service.reset();
	}

	async handleAddedAppointment(): Promise<void> {
		await this.setTimeCodeOptions(true);
		this.serviceOptions = this.getServiceOptions(this.form.controls.appointment.value);
	}

	// Populate service dropdown based off appointment selection
	getServiceOptions(appointment: Appointment): CustomerService[] {
		if (appointment === this.appointmentOptionDefault) {
			return [];
		}

		const services = appointment.services;
		services.sort((a, b) => a.desc > b.desc ? 1 : -1);

		return services;
	}

	async onClickTimeEntry(): Promise<void> {
		this.form = this.formHelper.markAsTouched(this.form);
		if (this.form.invalid) {
			await this.formHelper.alertUser({ header: this.mode + ' Time Entry Failed' });
			return;
		}

		const form = this.form.value;
		const timeEntry = {
			id: (this.timeEntry) ? this.timeEntry.id : 0,
			capital: form.capital,
			minutes: this.dateHelper.getMinutesFromHoursMinutes(this.form.controls.time.value),
			note: form.note,
			payCodeId: form.payCode.id,
			payCodeDesc: form.payCode.desc,
			customerServiceId: form.service.id,
			customerServiceDesc: form.service.desc,
			timeCodeId: form.timeCode.id,
			timeCodeDesc: form.timeCode.desc,
			time: (this.timeEntry) ? this.timeEntry.time : this.dateHelper.getNowISO(),
		} as TimeEntry;

		const apptId = (this.apptTimeEntry) ? this.apptTimeEntry.apptId : form.appointment.id;
		await this.timeEntryService.saveTimeEntry(
			await this.authService.getCurrentUserId(),
			apptId,
			timeEntry,
			this.mode,
			() => this.resolveRequest()
		);
	}

	resolveRequest() {
		this.toastTimeEntrySaved();
		this.dismiss();
	}

	private async toastTimeEntrySaved(): Promise<void> {
		const toast = await this.toastController.create({
			message: `Time entry saved.`,
			duration: 3000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}

	canShowServiceDropDown(): boolean {
		return this.form.controls.appointment.value !== this.appointmentOptionDefault
			&& this.serviceOptions.length !== 0;
	}

	async dismiss(): Promise<void> {
		await this.modalController.dismiss({
			dismissed: true,
		});
	}

}
